import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { WHY_SELL_IMAGE } from "../../lib/config";

const benefits = [
  {
    title: "No fees or commissions",
    body: "The offer we make is the amount you receive.",
  },
  {
    title: "Sell completely as-is",
    body: "No repairs, no cleanouts, no cleaning, no surveys on your dime — leave what you don’t want and take what you do.",
  },
  {
    title: "No listings or showings",
    body: "Your property never hits the market. No strangers, no signs.",
  },
  {
    title: "We solve title problems",
    body: "Back taxes, liens, inherited paperwork — our team works through it with the title company.",
  },
  {
    title: "Your timeline, not ours",
    body: "Close in two weeks or six months. You pick the date.",
  },
  {
    title: "Real people, straight answers",
    body: "You’ll talk to the owners — not a call center, not a middleman.",
  },
];

const WhySell = () => {
  return (
    <section
      id="why-sell"
      className="section-pad relative"
      style={{ background: "#FAF7F2" }}
      data-testid="why-sell-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Sticky image */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="relative rounded-3xl overflow-hidden"
                style={{
                  border: "1px solid rgba(198,161,91,0.35)",
                  boxShadow: "0 40px 60px -40px rgba(18,43,32,0.35)",
                }}
              >
                <img
                  src={WHY_SELL_IMAGE}
                  alt="Aerial view of rural land at golden hour"
                  className="w-full h-[420px] md:h-[540px] object-cover"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(18,43,32,0) 55%, rgba(18,43,32,0.55) 100%)",
                  }}
                />
                <div className="absolute bottom-6 left-6 right-6">
                  <p
                    className="eyebrow"
                    style={{ color: "#C6A15B" }}
                  >
                    The Ibex Difference
                  </p>
                  <p
                    className="font-heading text-[#FAF7F2] mt-2"
                    style={{ fontSize: "1.35rem", lineHeight: 1.25 }}
                  >
                    Straight numbers. Kept promises.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Benefits list */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <p className="eyebrow">The Ibex Difference</p>
              <h2
                className="font-heading font-medium mt-4"
                style={{
                  fontSize: "clamp(2rem, 4vw, 3.4rem)",
                  lineHeight: 1.1,
                  color: "#122B20",
                  letterSpacing: "-0.015em",
                }}
              >
                A Better Way <span className="gold-italic">To Sell.</span>
              </h2>
            </motion.div>

            <motion.ul
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.08 } },
              }}
              className="mt-10 space-y-6"
            >
              {benefits.map((b, i) => (
                <motion.li
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    show: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="flex gap-4 md:gap-5 items-start pb-6"
                  style={{ borderBottom: "1px solid rgba(168,162,154,0.35)" }}
                  data-testid={`why-benefit-${i}`}
                >
                  <div
                    className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                    style={{
                      background: "rgba(198,161,91,0.14)",
                      border: "1px solid rgba(198,161,91,0.45)",
                    }}
                  >
                    <Check size={16} color="#C6A15B" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <h3
                      className="font-heading font-medium"
                      style={{ color: "#122B20", fontSize: "1.15rem" }}
                    >
                      {b.title}
                    </h3>
                    <p
                      className="mt-1.5 font-body text-[#2B2926]/80"
                      style={{ fontSize: "0.95rem", lineHeight: 1.7 }}
                    >
                      {b.body}
                    </p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhySell;
